import React, { useEffect, useState, useMemo } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { trackJobView, trackCompanyLink, trackJobApply, trackJobSave, trackJobUnsave, trackEvent } from '../../../../utils/analytics/analytics';
import type { AnalyticsEvent } from '../../../../types/analytics-events';
import { CaretDown, CaretUp, Info, MapPin, WifiHigh, CheckCircle } from '@phosphor-icons/react';
import { localisedRouteGenerator } from '../../../../i18n/routes/routeGenerator';
import { generateJobUrl } from '../../../../utils/routing/generate-url';
import { BASE_URL, LOCALIZED_PATHS } from '../../../../utils/constants/url-constants';
import { getDevSiteOrigin } from '../../../../utils/constants/dev-site-origin';
import { Link } from '../../../../components/Link';
import type { DisplayJob } from '../../../../types';
import {
  resolveJobDisplayDescriptionFromDisplayJob,
  shouldShowJobDescriptionSourceDebug,
} from '../../../../utils/job/pick-job-display-description';
import type { FinalJob } from '../../../../lambda/types';
import { generateJobPostingSchema } from '../../../../utils/seo/generate-job-posting-schema';
// normalizeJobDetail is now used by useJobNormalization hook
import { generateCanonicalUrls } from '../../../../utils/seo/generate-canonical-urls';
import { useData } from "vike-react/useData";
import { usePageContext } from "vike-react/usePageContext";
import type { Data } from "./+data.js";
import { useAppSelector, useAppDispatch, useAppStore } from '../../../../redux/hooks';
import { ApplicationState, updateJobApplicationState, updateJobHiddenState } from '../../../../redux/features/jobs/jobsSlice';
import { ApplicationStateSelector } from '../../../../components/ApplicationStateSelector';
import { APPLICATION_STATES } from '../../../../components/application-state';
import { useToast } from '../../../../contexts/ToastContext';
import { getIndexName } from '../../../../constants';
import { generateInternalLinks, generateContextualLinks, generateRelatedContentLinks } from '../../../../utils/seo/internal-linking';
import { fixMojibake } from '../../../../utils/text/fix-mojibake';
import { CompanyName } from '../../../../components/CompanyName';
import { useSavedJobActions } from '../../../../hooks/useSavedJobActions';
import { useUserJobStateSync } from '../../../../hooks/useUserJobStateSync';
import type { ResolvedJobMatch } from '../../../../types/job-match';
import { resolveJobMatchScore } from '../../../../utils/job-match/resolve-job-match-score';
import { useAuth } from '../../../../hooks/useAuth';
import { formatDate, calculateEstimatedExpiryDate, calculateDaysAgo } from './utils/dateUtils';
import { isDark, isWhiteOrNearWhite, constructApplyUrl } from './utils/jobDetailUtils';
import { transformJobForLanguage, getIconForItem, scrollToSection, getLocationsForJobDetailDisplay, prioritizeLocations, isLocationRemotenessDisplayRedundant, getFirstCompanyUrl, extractYouTubeVideoId, isValidNumber, safeParseNumber, hasAnyValidNumber, getValidNumberOrNull, calculateCompanyAge, formatCompanyAge, getSocialIcon, calculateBrandStyles, generateMetaDescription, generateJobKeywords } from './utils';
import { convertSalaryToMonthlyEUR, hasOriginalSalary, getCurrencySymbol, type OriginalSalary } from './utils/salaryConversion';
import { estimateFinnishNetSalary } from './utils/estimateFinnishNetSalary';
import { readChartsCollapseCookie, writeChartsCollapseCookie } from './utils/chartsCollapse';
import { calculateHasAnyCharts } from './utils/chartsUtils';
import {
  readSalaryContextCollapsedPreference,
  writeSalaryContextCollapsedPreference,
} from './utils/job-detail-ui-preferences';
import { CompanyLogo, JobActionButtons, TabContent, MoneyGraph, MonthlyHiringChart, StarburstChart, SocialMediaButtons, LocationRemotenessChart, SalaryStatisticsChart, SalaryDistributionChart, RoleRoleLevelChart, DonutSunburstChart, CompanyDistributionChart, LanguageSwitcher, JobHeader, JobDetailsGrid, LocationRemotenessSection, JobSalarySourceBadge, LanguagesSpecialisationsSection, CompanySection, JobDetailsSection, InternalLinksSection, EllipsesMenuDropdown, ApplicationDeadlineSection, MatchScoreBreakdown, ContactEmailSection, OverviewSectionHeader, ApplyButton, JobDescriptionSection, JobNotFound404, ExpiredJobBanner, JobDetailSkeleton, TyomarkkinatoriDataSourceAttribution, TailorCvModal } from './components';
import { SimilarJobsSection } from './components/SimilarJobsSection';
import { BreadcrumbNavigation } from '../../../../components/BreadcrumbNavigation';
import { sharePage } from './utils/shareUtils';
import { useDebugRenderIf } from '../../../../utils/debug/debug-render';
import { useJobState } from './hooks/useJobState';
import { useJobActions } from './hooks/useJobActions';
import { useJobNormalization } from './hooks/useJobNormalization';
import { useLanguageUrls } from './hooks/useLanguageUrls';
import { useCardDesignVariant } from '../../../../hooks/useCardDesignVariant';
import { JobDetailPageV2 } from './v2/JobDetailPageV2';
import { useSalaryDisplay } from './hooks/useSalaryDisplay';
import { buildSrpFacetHref } from './utils/buildSrpFacetHref';
import { resolveJobPageBreadcrumbModel } from './utils/resolve-job-page-breadcrumb';
import { resolveJobDetailData } from './utils/resolveJobDetailData';
import { fetchSimilarJobsAndLocationCountFromAlgolia } from '../../../../utils/job-detail/similar-jobs-algolia';
import { buildSimilarJobsSrpHref } from './utils/buildSimilarJobsSrpHref';
import { resolveJobSalaryListingProvenance } from '../../../../utils/job-detail/resolve-job-salary-listing-provenance';
import { resolveJobSalarySourceBadgeKind } from '../../../../utils/job-detail/resolve-job-salary-source-badge-kind';
import { resolveSalaryInferenceExplanationKind } from '../../../../utils/job-detail/resolve-salary-inference-explanation-kind';
import { formatSalaryInferenceExplanation } from '../../../../utils/job-detail/salary-inference-explanation-intl';
import { formatSalaryMinMaxPair } from '../../../../utils/data/format-salary-min-max-pair';
import { SalaryInferenceExplainer } from '../../../../components/SalaryInferenceExplainer';
import {
  resolveJobUrlCompany,
  resolveJobUrlLocation,
  resolveJobUrlRemoteness,
  resolveJobUrlTitle,
} from '../../../../utils/routing/job-url-input';

// Import the Footer component
import Footer from '../../../../components/Footer';
import { EngagementStrip } from '../../../../components/engagement/EngagementStrip';

//TODO:: Fetch object id from all 3 indices so I can generate all 3 urls

function CheckCircleSolidApplied({ className }: { className?: string }) {
  return <CheckCircle weight="fill" className={className} />;
}

/** Read serialized page context from window so first client paint has data and matches server (avoids hydration mismatch). */
function getSerializedData(): Data | undefined {
  if (typeof window === 'undefined') return undefined;
  const pc = (window as { __VITE_PAGE_CONTEXT__?: { data?: Data } }).__VITE_PAGE_CONTEXT__;
  return pc?.data;
}

export interface JobDetailPageProps {
  /** When mounted in the job island (static→interactive swap), data is passed in; no useData. */
  initialData?: Data;
  /** Locale when mounted in island (no usePageContext). */
  initialLocale?: string;
}

const JobDetailPage: React.FC<JobDetailPageProps> = ({ initialData, initialLocale }) => {
  const dataFromHook = useData<Data>();
  const pageContext = usePageContext();
  const jobShareBaseUrl = import.meta.env.DEV
    ? getDevSiteOrigin({
        requestUrl:
          typeof (pageContext as { urlOriginal?: string } | undefined)?.urlOriginal === "string"
            ? (pageContext as { urlOriginal: string }).urlOriginal
            : undefined,
      })
    : BASE_URL;
  // Use serialized data on first client paint so we match server output and avoid hydration crash (useData/pageContext can be delayed).
  // When mounted in the job island, initialData is provided.
  const data = useMemo(
    () =>
      resolveJobDetailData([
        initialData,
        dataFromHook,
        (pageContext as { data?: Data } | undefined)?.data,
        getSerializedData(),
      ]),
    [initialData, dataFromHook, pageContext],
  );
  // Guard: during client-side navigation pageContext can be briefly undefined; avoid reading routeParams until ready.
  // When in island, derive from initialData.job.
  const routeParams = pageContext?.routeParams as { jobId?: string; companyId?: string; seoSlug?: string } | undefined;
  const routeParamsFromJob =
    initialData?.job && typeof initialData.job === 'object' && initialData.job !== null
      ? {
          jobId: String((initialData.job as { objectID?: unknown }).objectID ?? ''),
          companyId: String((initialData.job as { companyId?: unknown }).companyId ?? (initialData.job as { company?: unknown }).company ?? ''),
          seoSlug: (initialData as { seoSlug?: string }).seoSlug ?? '',
        }
      : undefined;
  const effectiveRouteParams = routeParams ?? routeParamsFromJob;
  const seoSlug = effectiveRouteParams?.seoSlug;
  // Safely destructure data - it can be undefined during client-side navigation transitions
  const { job, httpStatusCode, heroImage: serverHeroImage, heroImageHasTransparency, hasAnyCharts: serverHasAnyCharts, similarJobs, locationJobCount } = data || {
    job: undefined,
    httpStatusCode: undefined,
    heroImage: undefined,
    heroImageHasTransparency: undefined,
    hasAnyCharts: false,
    similarJobs: [],
    locationJobCount: null,
  };
  useEffect(() => {
    if (typeof window === 'undefined') return;
    console.log('[SimilarJobsDebug][+Page] render-data', {
      objectId: (job as { objectID?: string } | undefined)?.objectID ?? 'none',
      initialDataProvided: !!initialData,
      similarCount: Array.isArray(similarJobs) ? similarJobs.length : -1,
      locationJobCount,
    });
  }, [job, similarJobs, locationJobCount, initialData]);
  const intl = useIntl();

  // Use pageContext.locale for SSR compatibility (available during SSR), fallback to intl.locale for client. In island, use initialLocale.
  const jobLocale = (initialLocale ?? pageContext?.locale ?? intl.locale ?? 'en') as string;
  // SRP card + job detail page redesign A/B test — called unconditionally (before the early
  // returns below) per rules of hooks. See hooks/useCardDesignVariant.ts.
  const cardDesignVariant = useCardDesignVariant();

  const showDescriptionSourceDebug = useMemo(() => {
    const urlString =
      typeof window !== 'undefined'
        ? window.location.href
        : (pageContext as { urlOriginal?: string })?.urlOriginal ?? '';
    return shouldShowJobDescriptionSourceDebug(urlString);
  }, [pageContext]);

  // Use extracted useJobNormalization hook
  const { displayJob, finalJob, normalizationMeta } = useJobNormalization({
    job,
    jobLocale,
    seoSlug,
  });

  const [clientSimilarJobs, setClientSimilarJobs] = useState<Data['similarJobs'] | null>(null);
  const similarJobsFetchAttemptedRef = React.useRef<Set<string>>(new Set());

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const objectId = String((displayJob as { objectID?: unknown } | undefined)?.objectID ?? '');
    if (!objectId) return;

    const hasServerSimilarJobs = Array.isArray(similarJobs) && similarJobs.length > 0;
    if (hasServerSimilarJobs) {
      setClientSimilarJobs(null);
      return;
    }
    if (similarJobsFetchAttemptedRef.current.has(objectId)) return;

    similarJobsFetchAttemptedRef.current.add(objectId);
    const primaryLocation = Array.isArray(displayJob?.location)
      ? String(displayJob.location[0] ?? '')
      : String(displayJob?.location ?? '');
    const locale = (['en', 'fi', 'sv'].includes(jobLocale) ? jobLocale : 'en') as 'en' | 'fi' | 'sv';

    fetchSimilarJobsAndLocationCountFromAlgolia({
      jobObjectId: objectId,
      normalisedTitle: (displayJob as { normalisedTitle?: string } | undefined)?.normalisedTitle,
      primaryLocation,
      jobLocale: locale,
      indexName: getIndexName(locale),
      baseUrl: window.location.origin,
    })
      .then(({ similarJobs: fetchedSimilarJobs }) => {
        console.log('[SimilarJobsDebug][+Page] client-backfill', {
          objectId,
          fetchedCount: Array.isArray(fetchedSimilarJobs) ? fetchedSimilarJobs.length : -1,
        });
        setClientSimilarJobs(Array.isArray(fetchedSimilarJobs) ? fetchedSimilarJobs : []);
      })
      .catch(() => {
        console.log('[SimilarJobsDebug][+Page] client-backfill-failed', { objectId });
        setClientSimilarJobs([]);
      });
  }, [displayJob, jobLocale, similarJobs]);

  const effectiveSimilarJobs = useMemo(() => {
    const sourceSimilarJobs = Array.isArray(similarJobs) ? similarJobs : [];
    if (sourceSimilarJobs.length > 0) return sourceSimilarJobs;
    return Array.isArray(clientSimilarJobs) ? clientSimilarJobs : sourceSimilarJobs;
  }, [similarJobs, clientSimilarJobs]);

  const similarJobsSeeAllHref = useMemo(() => {
    const primaryLocation = Array.isArray(displayJob?.location)
      ? String(displayJob?.location?.[0] ?? '')
      : String(displayJob?.location ?? '');
    return buildSimilarJobsSrpHref({
      locale: jobLocale,
      normalisedTitle: displayJob?.normalisedTitle || displayJob?.title || '',
      primaryLocation,
    });
  }, [displayJob, jobLocale]);

  // Charts collapse state - read from cookie on mount (client-side only)
  const [chartsCollapsed, setChartsCollapsed] = useState<boolean>(false);

  useEffect(() => {
    // Read initial state from cookie (client-side only)
    const cookieValue = readChartsCollapseCookie();
    if (cookieValue !== null) {
      setChartsCollapsed(cookieValue);
    }
  }, []);

  const toggleChartsCollapse = () => {
    const newState = !chartsCollapsed;
    setChartsCollapsed(newState);
    writeChartsCollapseCookie(newState);
  };

  // Original job text display state
  const [showOriginalJobText, setShowOriginalJobText] = useState<boolean>(false);

  // Company name copy state
  const [companyNameCopied, setCompanyNameCopied] = useState<boolean>(false);

  // Use extracted useJobState hook
  const { jobId, isSaved, isApplied, isHidden, currentApplicationState, isToolsPage } = useJobState({ displayJob });

  // Get toast and other dependencies
  const { showToast } = useToast();
  const { isAuthenticated, signIn } = useAuth();
  const userProfile = useAppSelector((state) => state.jobForm);
  const comparisonPreferences = useAppSelector((state) => state.comparisonPreferences);
  const savedSearches = useAppSelector((state) => state.recentSearches?.savedSearches ?? []);
  const userState = useAppSelector((state) => state.userState);
  const { recordViewedJob, setAppliedJobState } = useUserJobStateSync();
  const { saveJob, unsaveJob } = useSavedJobActions();

  // Only calculate if state is hydrated (to avoid SSR/client mismatch)
  const isHydrated = userState.hydratedFromSSR || (typeof window !== 'undefined');

  const breadcrumbSearchReturnHref = null;

  // Generate job URL for share functionality
  const jobUrl = useMemo(() => {
    if (!displayJob) return '';
    return generateJobUrl({
      baseUrl: jobShareBaseUrl,
      jobId: displayJob.objectID.toString(),
      locale: intl.locale as any,
      title: resolveJobUrlTitle(displayJob),
      company: resolveJobUrlCompany(displayJob),
      location: resolveJobUrlLocation(displayJob),
      remoteness: resolveJobUrlRemoteness(displayJob),
    });
  }, [displayJob, intl.locale, jobShareBaseUrl]);

  // Use extracted useJobActions hook
  const { handleToggleSaved, handleToggleApplied, handleToggleHide, handleSetApplicationState, handleShare } = useJobActions({
    displayJob,
    jobId,
    isSaved,
    isApplied,
    isHidden,
    showToast,
    url: jobUrl,
  });

  // Ellipses menu state
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const menuRef = React.useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!menuRef.current?.contains(target)) {
        setIsMenuOpen(false);
      }
    };
    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isMenuOpen]);

  // Get the state to display in the ellipsis button
  const ellipsisButtonState = React.useMemo(() => {
    if (currentApplicationState) {
      const stateConfig = APPLICATION_STATES.find(s => s.value === currentApplicationState);
      return stateConfig ? {
        label: intl.formatMessage({ id: stateConfig.labelKey, defaultMessage: stateConfig.defaultLabel }),
        icon: stateConfig.iconSolid,
        color: stateConfig.color,
      } : null;
    }
    if (isApplied) {
      return {
        label: intl.formatMessage({ id: 'tools.applicationState.applied', defaultMessage: 'Applied', description: 'Application state label indicating job has been applied to' }),
        icon: CheckCircleSolidApplied,
        color: 'blue',
      };
    }
    return null;
  }, [currentApplicationState, isApplied, intl]);

  // Refs for scrolling to sections
  const overviewRef = React.useRef<HTMLDivElement>(null);
  const companyRef = React.useRef<HTMLDivElement>(null);
  const jobDetailsRef = React.useRef<HTMLDivElement>(null);

  // Debug: Track renders and navigation state
  useDebugRenderIf('JobDetailPage', {
    jobId: displayJob?.objectID,
    hasJob: !!displayJob,
    locale: jobLocale,
  });

  // Match score: profile when filled; otherwise infer from persisted recent searches
  const resolvedJobMatch = React.useMemo((): ResolvedJobMatch => {
    if (!displayJob || !isHydrated) {
      return { breakdown: null, source: null, searchInferenceBullets: [] };
    }
    return resolveJobMatchScore({
      job: displayJob,
      userProfile,
      comparisonPreferences,
      savedSearches,
      isHydrated,
    });
  }, [displayJob, isHydrated, userProfile, comparisonPreferences, savedSearches]);

  const matchScoreData = resolvedJobMatch.breakdown;
  const matchScoreMeta =
    resolvedJobMatch.source === 'recent-searches'
      ? ({ source: 'recent-searches' as const, bullets: resolvedJobMatch.searchInferenceBullets })
      : null;

  // Create save handler
  const handleSave = React.useCallback(async () => {
    if (!displayJob) return;
    const payload = { ...displayJob, id: displayJob.objectID };
    if (!isSaved) {
      trackJobSave({
        jobId: displayJob.objectID,
        jobTitle: displayJob.normalisedTitle || displayJob.title || '',
        locale: intl.locale,
      });
      const success = await saveJob(payload);
      if (success) {
        showToast(
          intl.formatMessage({ id: 'hit.toast.saved', defaultMessage: 'Job saved', description: 'Success toast notification message shown when a job is saved' }),
          'success'
        );
      }
    } else {
      trackJobUnsave({
        jobId: displayJob.objectID,
        jobTitle: displayJob.normalisedTitle || displayJob.title || '',
        locale: intl.locale,
      });
      const success = await unsaveJob(payload);
      if (success) {
        showToast(
          intl.formatMessage({ id: 'hit.toast.unsaved', defaultMessage: 'Job removed from saved', description: 'Info toast notification message shown when a job is removed from saved jobs' }),
          'info'
        );
      }
    }
  }, [displayJob, isSaved, saveJob, unsaveJob, showToast, intl]);

  // const routerLocation = useLocation(); // Commented out react-router-dom
  // Get sendEvent from location state if passed from Hit
  // const sendEvent = routerLocation.state?.sendEvent; // Commented out

  const [showTailorCvModal, setShowTailorCvModal] = useState(false);
  const handleOpenTailorCv = React.useCallback(() => {
    trackEvent({
      name: 'tailor_cv_interaction',
      params: {
        action: 'open',
        job_id: displayJob?.objectID,
        locale: intl.locale,
      },
    });
    setShowTailorCvModal(true);
  }, [displayJob, intl.locale]);
  const [showContactEmail, setShowContactEmail] = useState(false);
  const [showCompanyContactDetails, setShowCompanyContactDetails] = useState(false);
  const [showCompanyLocationMap, setShowCompanyLocationMap] = useState(false);
  const [companyInfoActiveTab, setCompanyInfoActiveTab] = useState<
    'financial' | 'hiring' | 'roles' | 'locations' | 'languages' | 'map'
  >('financial');
  const [showOriginalSalary, setShowOriginalSalary] = useState(false);
  // Keep SSR and first client render identical: collapsed by default, then hydrate from localStorage.
  const [compensationExpanded, setCompensationExpanded] = useState(false);
  const [hasHydratedUiPreferences, setHasHydratedUiPreferences] = useState(false);
  useEffect(() => {
    setCompensationExpanded(!readSalaryContextCollapsedPreference());
    setHasHydratedUiPreferences(true);
  }, []);
  useEffect(() => {
    if (!hasHydratedUiPreferences) return;
    writeSalaryContextCollapsedPreference(!compensationExpanded);
  }, [compensationExpanded, hasHydratedUiPreferences]);
  // Remount salary charts after a short delay so they paint in the visible container (island swap).
  const [salaryChartRemountKey, setSalaryChartRemountKey] = useState(0);
  useEffect(() => {
    const t = setTimeout(() => setSalaryChartRemountKey((k) => k + 1), 150);
    return () => clearTimeout(t);
  }, []);

  // Calculate estimated expiry date if applicationDeadline is missing (35 days from postedDate)
  // This hook must be called before any early returns to follow Rules of Hooks
  const estimatedExpiryDate = React.useMemo(() => {
    if (!displayJob) return null;
    return calculateEstimatedExpiryDate(displayJob.applicationDeadline, displayJob.postedDate);
  }, [displayJob]);

  // Track salary insight view (once per job)
  const salaryInsightTrackedRef = React.useRef<string | null>(null);

  useEffect(() => {
    if (displayJob?.objectID && displayJob.objectID !== salaryInsightTrackedRef.current) {
      const hasSalaryInsight =
        typeof displayJob.salaryRangeMin === 'number' &&
        typeof displayJob.salaryRangeMax === 'number' &&
        displayJob.salaryRangeMin > 0 &&
        displayJob.salaryRangeMax > 0;

      if (hasSalaryInsight) {
        const listingProv = resolveJobSalaryListingProvenance({
          salaryRangeMin: displayJob.salaryRangeMin,
          salaryRangeMax: displayJob.salaryRangeMax,
          salaryDetails: displayJob.salaryDetails,
        });
        const hasExactSalary = listingProv === 'employer_quote';
        const hasEstimatedSalary = listingProv !== 'employer_quote';

        trackEvent({
          name: 'salary_insight_view',
          params: {
            job_id: displayJob.objectID,
            company_id: displayJob.companyId || '',
            has_exact_salary: hasExactSalary,
            has_estimated_salary: hasEstimatedSalary,
            locale: intl.locale,
          },
        });

        salaryInsightTrackedRef.current = displayJob.objectID;
      }
    }
  }, [displayJob, intl.locale]);

  // Effect to handle side effects (tracking, etc.) - only run once per job
  useEffect(() => {
    // Handle side effects (tracking, etc.) - only run once per job
    if (job?.objectID && displayJob) {
      void recordViewedJob({ id: job.objectID, ...displayJob });

      // Track job view with typed event
      const hasSalaryInsight =
        typeof displayJob.salaryRangeMin === 'number' &&
        typeof displayJob.salaryRangeMax === 'number' &&
        displayJob.salaryRangeMin > 0 &&
        displayJob.salaryRangeMax > 0;

      const locationLabel = Array.isArray(displayJob.location) && displayJob.location.length > 0
        ? displayJob.location[0]
        : (typeof displayJob.location === 'string' ? displayJob.location : undefined);

      trackEvent({
        name: 'job_view',
        params: {
          job_id: job.objectID,
          job_title: displayJob.normalisedTitle || displayJob.title || '',
          company_id: displayJob.companyId || '',
          location_label: locationLabel,
          is_remote: displayJob.remoteness === 'remote' || displayJob.remoteness === 'hybrid',
          has_salary_insight: hasSalaryInsight,
          locale: intl.locale,
        },
      });
    }
  }, [displayJob, job?.objectID, recordViewedJob, intl.locale]);


  // No data from useData() or pageContext yet: show skeleton (avoid blank page on client nav / delayed hydration).
  const hasData = data != null;
  const hasJob = job != null && job !== undefined;
  if (!hasData && effectiveRouteParams?.jobId) {
    return <JobDetailSkeleton />;
  }

  // Data has loaded but job is missing: show not found only when we're sure (data resolved, no job).
  if (hasData && !hasJob) {
    return <JobNotFound404 />;
  }

  // Still waiting for displayJob normalization: render nothing to avoid skeleton CLS.
  if (hasJob && (displayJob === undefined || displayJob === null)) {
    return null;
  }

  if (!displayJob) {
    return null;
  }

  const {
    title,
    description,
    originalDescription,
    postedDate,
    lastSeen,
    field,
    jobType,
    location,
    remoteness,
    companyId,
    requiredLanguages,
    optionalLanguages,
    salaryRangeMin,
    salaryRangeMax,
    salaryPeriod,
    salaryDetails,
    currency,
    peopleManagementTier,
    specialisations,
    applicationDeadline,
    contactEmail,
    url,
    normalisedRoleLevel,
    normalisedTitle,
    brandColour,
    companyIndustry,
    listingCompanyIndustry,
    employerOverview,
    companyHQInFinland,
    companyCoreValues,
    companyAwardsAndCertifications,
    companyContactDetails,
    companyHeadCountMin,
    companyHeadCountMax,
    companyHeadcountRangeMin,
    companyHeadcountRangeMax,
    companyAnnualRevenueRangeMin,
    companyAnnualRevenueRangeMax,
    officeImages,
    socialMediaLinks,
    vacationWeeks,
    minimumYearsOfExperience,
    perks,
    visaSponsor,
    // Additional company fields (using new prefixed names to avoid collisions)
    companyRevenue,
    companyProfit,
    companyProfitMargin,
    companyVideo,
    linkedinUrl,
    facebookUrl,
    instagramUrl,
    youtubeUrl,
    tiktokUrl,
    registryMainBusinessLineDescriptionEn,
    registryMainBusinessLineDescriptionFi,
    registryMainBusinessLineDescriptionSv,
    registryCompanyFormDescription,
    registryWebsite,
    registryMainName,
    registryStreet,
    registryCity,
    registryPostCode,
    registryBuildingNumber,
    address, // Address field from Algolia
    address_fi,
    address_sv,
    companyAbout,
    companyMonthlyCounts,
    companyHiringTrends,
    companyHiringTrendsByLocale,
    locationSalaryStats,
    businessID,
    registryBusinessId,
    registryRegistrationDate,
    originalJobLanguage,
    companyGeocoordinates,
    isFinland,
  } = displayJob as DisplayJob;

  const resolvedJobBody = resolveJobDisplayDescriptionFromDisplayJob({
    originalDescription,
    description,
  });
  const displayDescription = resolvedJobBody.body;
  const jobDescriptionBodySource = resolvedJobBody.source;

  // Use salary display hook
  const { displaySalary, convertedSalary, shouldShowOriginalButton } = useSalaryDisplay({
    displayJob,
    showOriginalSalary,
  });

  const finnishNetSalaryEstimate = useMemo(
    () => (isFinland ? estimateFinnishNetSalary(convertedSalary) : null),
    [isFinland, convertedSalary],
  );
  const finnishNetSalaryExplanationText = useMemo(() => {
    if (!finnishNetSalaryEstimate) return '';
    return intl.formatMessage(
      {
        id: 'jobDetailPage.estimatedNetSalary.explanation',
        defaultMessage:
          'Estimated {year} take-home pay after Finnish income tax, pension, and unemployment insurance contributions. Assumes the national average municipal tax rate ({rate}%), no church tax, and standard deductions only — your actual net pay depends on your municipality, church membership, age, and family situation.',
        description: 'Explains the assumptions behind the estimated Finnish net (after-tax) salary figure shown near the gross compensation',
      },
      { year: finnishNetSalaryEstimate.taxYear, rate: finnishNetSalaryEstimate.assumedMunicipalRatePct },
    );
  }, [finnishNetSalaryEstimate, intl]);

  const salaryListingProvenance = resolveJobSalaryListingProvenance({
    salaryRangeMin,
    salaryRangeMax,
    salaryDetails,
  });

  const salaryInferenceExplanationKind = useMemo(
    () =>
      resolveSalaryInferenceExplanationKind({
        salaryIsEstimate: displayJob.salaryIsEstimate,
        salaryRangeMin,
        salaryRangeMax,
        salaryDetails,
      }),
    [displayJob.salaryIsEstimate, salaryRangeMin, salaryRangeMax, salaryDetails],
  );
  const salaryInferenceExplanationText = useMemo(
    () =>
      formatSalaryInferenceExplanation(
        salaryInferenceExplanationKind,
        intl.formatMessage,
        displayJob.salaryEstimateSampleSize,
      ),
    [salaryInferenceExplanationKind, intl, displayJob.salaryEstimateSampleSize],
  );
  const salarySourceBadgeKind = useMemo(
    () =>
      resolveJobSalarySourceBadgeKind({
        salaryIsEstimate: displayJob.salaryIsEstimate,
        salaryRangeMin,
        salaryRangeMax,
        salaryDetails,
      }),
    [displayJob.salaryIsEstimate, salaryRangeMin, salaryRangeMax, salaryDetails],
  );

  // Get trends for current locale, fallback to English if not available
  const currentLocaleTrends = companyHiringTrendsByLocale?.[jobLocale as 'en' | 'fi' | 'sv']
    || companyHiringTrendsByLocale?.en
    || companyHiringTrends;

  // Use server-side calculated hasAnyCharts to prevent CLS (Cumulative Layout Shift)
  // This value is calculated in +data.ts before the page renders. In the job island, serialized data
  // might omit it, so fallback to computing from job so charts always show in the final version.
  const hasAnyCharts =
    serverHasAnyCharts ??
    (job && typeof job === 'object'
      ? calculateHasAnyCharts(job as Parameters<typeof calculateHasAnyCharts>[0], jobLocale)
      : false);

  // Set default tab based on available data
  useEffect(() => {
    if (!chartsCollapsed) {
      const hasFinancial = hasAnyValidNumber(companyRevenue, companyProfit, companyProfitMargin);
      const hasHiring = !!(companyMonthlyCounts && Object.keys(companyMonthlyCounts).length > 0);
      const hasLocations = !!(currentLocaleTrends &&
        ((currentLocaleTrends.locationDistribution &&
          Object.keys(currentLocaleTrends.locationDistribution).length > 0 &&
          Object.values(currentLocaleTrends.locationDistribution).some(val => hasAnyValidNumber(val))) ||
          (currentLocaleTrends.remotenessDistribution &&
            (currentLocaleTrends.remotenessDistribution.remote > 0 ||
              currentLocaleTrends.remotenessDistribution.hybrid > 0 ||
              currentLocaleTrends.remotenessDistribution.onsite > 0))));
      const hasRoles = !!(currentLocaleTrends && (currentLocaleTrends.roleDistribution || currentLocaleTrends.roleLevelDistribution));

      // Check if current tab has data, if not switch to first available
      const currentTabHasData =
        (companyInfoActiveTab === 'financial' && hasFinancial) ||
        (companyInfoActiveTab === 'hiring' && hasHiring) ||
        (companyInfoActiveTab === 'roles' && hasRoles) ||
        (companyInfoActiveTab === 'locations' && hasLocations) ||
        (companyInfoActiveTab === 'languages' && !!(currentLocaleTrends?.languageRequirements &&
          Object.keys(currentLocaleTrends.languageRequirements).length > 0 &&
          Object.values(currentLocaleTrends.languageRequirements).some(val => hasAnyValidNumber(val)))) ||
        (companyInfoActiveTab === 'map' && !!companyGeocoordinates);

      if (!currentTabHasData) {
        if (hasFinancial) {
          setCompanyInfoActiveTab('financial');
        } else if (hasHiring) {
          setCompanyInfoActiveTab('hiring');
        } else if (hasLocations) {
          setCompanyInfoActiveTab('locations');
        } else if (hasRoles) {
          setCompanyInfoActiveTab('roles');
        }
      }
    }
  }, [chartsCollapsed, companyRevenue, companyProfit, companyProfitMargin, companyMonthlyCounts, currentLocaleTrends, companyGeocoordinates, companyInfoActiveTab]);

  // Use server-validated heroImage (only shown if it has optimal dimensions)
  const heroImage = serverHeroImage;

  // Construct applyUrl with UTM params for the apply button
  // For Tyomarkkinatori jobs, swap locale (fi -> en) when viewing English version
  const applyUrl = constructApplyUrl(url, companyId, jobLocale);

  const canonicalLocale = intl.locale === 'fi' || intl.locale === 'sv' ? intl.locale : 'en';
  const canonicalSource = normalizationMeta?.canonicalJob || finalJob;

  // Use language URLs hook
  const languageUrls = useLanguageUrls({
    canonicalSource,
    canonicalLocale,
  });

  const canonicalUrlForSchema = canonicalSource
    ? languageUrls?.current
    : (typeof window !== 'undefined' ? `${window.location.origin}${window.location.pathname}` : undefined);

  // Generate schema and badge status
  const { schema: structuredData, isContentComplete } = generateJobPostingSchema(
    displayJob,
    intl.locale || 'en',
    applyUrl,
    canonicalUrlForSchema,
    normalizationMeta?.schemaOverrides ?? {}
  );

  const formattedPostedDate = formatDate(postedDate, intl.locale);
  const formattedLastSeenDate = formatDate(lastSeen, intl.locale);
  const postedDaysAgo = calculateDaysAgo(postedDate);
  const lastSeenDaysAgo = calculateDaysAgo(lastSeen);
  const formattedApplicationDate = formatDate(applicationDeadline, intl.locale);
  const formattedEstimatedExpiryDate = estimatedExpiryDate ? formatDate(estimatedExpiryDate, intl.locale) : null;
  const mergeLocationRemotenessTiles = isLocationRemotenessDisplayRedundant(location, remoteness);

  const hasSecondaryJobDetailTiles =
    !!(field ||
      normalisedRoleLevel ||
      peopleManagementTier ||
      jobType ||
      (minimumYearsOfExperience != null && minimumYearsOfExperience > 0) ||
      visaSponsor);

  const hasPostedOnlyRow =
    !(formattedApplicationDate || formattedEstimatedExpiryDate) && Boolean(formattedPostedDate);

  const hasLanguagesOrSpecsCard =
    Boolean(
      (requiredLanguages && requiredLanguages.length > 0) ||
        (optionalLanguages && optionalLanguages.length > 0) ||
        (specialisations && specialisations.length > 0),
    );

  const hasOverviewRightColumn =
    Boolean(formattedApplicationDate || formattedEstimatedExpiryDate) ||
    hasSecondaryJobDetailTiles ||
    Boolean(matchScoreData) ||
    hasPostedOnlyRow ||
    hasLanguagesOrSpecsCard;

  const facetHref = (facetKey: string, value: string) => buildSrpFacetHref(jobLocale, facetKey, value);

  const jobBreadcrumbModel = useMemo(
    () => resolveJobPageBreadcrumbModel({ locale: jobLocale, location: displayJob.location }),
    [jobLocale, displayJob.location],
  );

  const collapsedSalarySnapshot = useMemo(() => {
    const stats =
      (displayJob?.companySalaryStats as any) ||
      (displayJob?.salaryStats as any) ||
      (displayJob?.allSalaryStats as any)?.titleRoleLevel ||
      (displayJob?.allSalaryStats as any)?.title ||
      null;
    if (!stats) return null;

    const p25Min = Number(stats?.percentile25?.min);
    const p75Max = Number(stats?.percentile75?.max);
    const medianMin = Number(stats?.median?.min);
    const medianMax = Number(stats?.median?.max);
    const sampleSize = Number(stats?.sampleSize ?? stats?.count ?? 0);

    const hasRange = Number.isFinite(p25Min) && p25Min > 0 && Number.isFinite(p75Max) && p75Max > p25Min;
    const hasMedian = Number.isFinite(medianMin) && medianMin > 0 && Number.isFinite(medianMax) && medianMax > 0;
    if (!hasRange && !hasMedian) return null;

    const marketMedian =
      hasMedian
        ? (medianMin + medianMax) / 2
        : hasRange
          ? (p25Min + p75Max) / 2
          : 0;

    const offerMid =
      displaySalary && Number.isFinite(displaySalary.min) && Number.isFinite(displaySalary.max)
        ? (displaySalary.min + displaySalary.max) / 2
        : null;

    return {
      p25Min: hasRange ? p25Min : marketMedian * 0.9,
      p75Max: hasRange ? p75Max : marketMedian * 1.1,
      marketMedian,
      offerMid,
      sampleSize: Number.isFinite(sampleSize) && sampleSize > 0 ? Math.round(sampleSize) : null,
    };
  }, [displayJob, displaySalary]);

  // Generate meta description and keywords (used only as a fallback inside generateJobPostingSchema)
  const metaDesc = generateMetaDescription(displayDescription);
  const jobKeywords = generateJobKeywords(
    intl.locale as 'fi' | 'en' | 'sv',
    title,
    companyId,
    location,
    companyIndustry,
    specialisations
  );

  // Calculate brand styles
  const brandStyles = React.useMemo(() => {
    return calculateBrandStyles({
      brandColour: brandColour,
      isTyomarkkinatori: displayJob?.isTyomarkkinatori,
    });
  }, [brandColour, displayJob?.isTyomarkkinatori]);

  const {
    effectiveBrandColour,
    useBrandColor,
    leftBorderStyle,
    applyBtnStyle,
    applyBtnHoverStyle,
    savedIconStyle,
  } = brandStyles;

  // Generate comprehensive internal links using our new utility.
  // Location/roleLevel/remoteness/jobType/requiredLanguages/field already have
  // dedicated facet pills elsewhere on this page, so we drop those categories
  // here to avoid showing the same link twice.
  const nonRedundantSearchCategories = new Set(['company', 'industry', 'skills', 'salary']);
  const internalLinks = generateInternalLinks(displayJob, intl.locale).filter((link) =>
    nonRedundantSearchCategories.has(link.category),
  );
  const contextualLinks = generateContextualLinks(displayJob, intl.locale);
  const contentLinks = generateRelatedContentLinks(displayJob, intl.locale);

  const priorityOrder = { high: 3, medium: 2, low: 1 } as const;
  const searchLinks = [...internalLinks, ...contextualLinks].sort(
    (a, b) => priorityOrder[b.priority] - priorityOrder[a.priority],
  );
  const allLinks = [...contentLinks.slice(0, 4), ...searchLinks].slice(0, 8);

  const expiredJobBackHref = breadcrumbSearchReturnHref ?? (jobLocale === 'en' ? '/' : `/${jobLocale}`);

  return (
    <div className="min-h-screen bg-[#F8F5F0] dark:bg-[#170F07]" suppressHydrationWarning>
      <div className="max-w-6xl mx-auto p-2 pt-2 pb-6 sm:p-6">
            {httpStatusCode === 410 ? <ExpiredJobBanner backHref={expiredJobBackHref} /> : null}
            <BreadcrumbNavigation
              showHome={false}
              items={[
                {
                  label: intl.formatMessage({
                    id: 'breadcrumb.jobs',
                    defaultMessage: 'Jobs',
                    description:
                      'Breadcrumb link to the jobs listing hub',
                  }),
                  href: breadcrumbSearchReturnHref ?? jobBreadcrumbModel.jobsHref,
                },
                ...(jobBreadcrumbModel.locationHref && jobBreadcrumbModel.locationLabel
                  ? [
                      {
                        label: jobBreadcrumbModel.locationLabel,
                        href: jobBreadcrumbModel.locationHref,
                      },
                    ]
                  : []),
                { label: title },
              ]}
              trackHomeClick={true}
              trackData={{ jobId: displayJob?.objectID }}
            />

            {languageUrls && (
              <LanguageSwitcher
                languageUrls={languageUrls}
                canonicalLocale={canonicalLocale}
                originalJobLanguage={originalJobLanguage}
              />
            )}

            {/* Main content area - core job entity block */}
            {cardDesignVariant === 'new' ? (
              <JobDetailPageV2
                displayJob={displayJob}
                title={title}
                normalisedTitle={normalisedTitle}
                companyId={companyId}
                location={Array.isArray(location) ? location[0] : location}
                locationForChart={Array.isArray(location) ? location : location ? [location] : undefined}
                remoteness={remoteness}
                field={field}
                jobType={jobType}
                normalisedRoleLevel={normalisedRoleLevel}
                requiredLanguages={requiredLanguages}
                peopleManagementTier={peopleManagementTier}
                educationLevel={displayJob.educationLevel}
                minimumYearsOfExperience={minimumYearsOfExperience}
                collectiveAgreementId={displayJob.collectiveAgreementId}
                collectiveAgreementName={displayJob.collectiveAgreementName}
                collectiveAgreementMatchType={displayJob.collectiveAgreementMatchType}
                collectiveAgreementReasoning={displayJob.collectiveAgreementReasoning}
                jobLocale={jobLocale}
                displayDescription={displayDescription}
                overviewHeaderProps={{
                  displayJob,
                  isAuthenticated,
                  isHydrated,
                  matchScoreData,
                  matchScoreMeta,
                  onLoginClick: () => signIn(),
                  isSaved,
                  isApplied,
                  isHidden,
                  currentApplicationState,
                  ellipsisButtonState,
                  isMenuOpen,
                  menuRef,
                  effectiveBrandColour,
                  useBrandColor,
                  onSave: handleSave,
                  onToggleHide: handleToggleHide,
                  onShare: handleShare,
                  onSetApplicationState: handleSetApplicationState,
                  onToggleApplied: handleToggleApplied,
                  onMenuToggle: () => setIsMenuOpen(!isMenuOpen),
                }}
                applyUrl={applyUrl}
                isApplied={isApplied}
                setAppliedJobState={setAppliedJobState}
                formattedPostedDate={formattedPostedDate}
                postedDaysAgo={postedDaysAgo}
                formattedLastSeenDate={formattedLastSeenDate}
                formattedApplicationDate={formattedApplicationDate}
                formattedEstimatedExpiryDate={formattedEstimatedExpiryDate}
                matchScoreData={matchScoreData}
                salaryRangeMin={salaryRangeMin}
                salaryRangeMax={salaryRangeMax}
                currency={currency}
                salaryPeriod={salaryPeriod}
                salaryListingProvenance={salaryListingProvenance}
                optionalLanguages={optionalLanguages}
                perks={perks}
                jobDetailsRef={jobDetailsRef}
                currentLocaleTrends={currentLocaleTrends}
                allLinks={allLinks}
                effectiveSimilarJobs={effectiveSimilarJobs}
                similarJobsSeeAllHref={similarJobsSeeAllHref}
              />
            ) : (
            <main id="job-main-content" className="lg:flex lg:gap-6 lg:items-start">
              <div className="flex-1 min-w-0">
                <div
                  className={
                    'mt-6 ' +
                    'bg-white/90 dark:bg-[#1F1509]/95 shadow dark:shadow-none rounded-[28px] px-4 md:px-10 py-10 space-y-6 transition-colors duration-300 border border-[#DDD5C8]/70 dark:border-[#4A3520]/80 overflow-hidden'
                  }
                  style={leftBorderStyle}
                >
                  {/* SECTION 1: Overview */}
                  <div ref={overviewRef} data-section="overview" className="scroll-mt-20" id="overview">
                    <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3 mb-4">
                      <div className="min-w-0 flex-1">
                        <JobHeader
                          title={title}
                          normalisedTitle={normalisedTitle}
                          companyId={companyId}
                          companyDisplayName={displayJob.companyName ?? companyId}
                          registryCompanyFormDescription={registryCompanyFormDescription}
                          location={location}
                          isTyomarkkinatori={displayJob?.isTyomarkkinatori}
                        />
                      </div>
                      <OverviewSectionHeader
                        displayJob={displayJob}
                        isAuthenticated={isAuthenticated}
                        isHydrated={isHydrated}
                        matchScoreData={matchScoreData}
                        matchScoreMeta={matchScoreMeta}
                        onLoginClick={() => signIn()}
                        isSaved={isSaved}
                        isApplied={isApplied}
                        isHidden={isHidden}
                        currentApplicationState={currentApplicationState}
                        ellipsisButtonState={ellipsisButtonState}
                        isMenuOpen={isMenuOpen}
                        menuRef={menuRef}
                        effectiveBrandColour={effectiveBrandColour}
                        useBrandColor={useBrandColor}
                        onSave={handleSave}
                        onToggleHide={handleToggleHide}
                        onShare={handleShare}
                        onSetApplicationState={handleSetApplicationState}
                        onToggleApplied={handleToggleApplied}
                        onMenuToggle={() => setIsMenuOpen(!isMenuOpen)}
                      />
                    </div>

                    {!hasOverviewRightColumn ? (
                      <ApplyButton
                        applyUrl={applyUrl}
                        isApplied={isApplied}
                        displayJob={displayJob}
                        applyBtnStyle={applyBtnStyle}
                        applyBtnHoverStyle={applyBtnHoverStyle}
                        effectiveBrandColour={effectiveBrandColour}
                        onSetApplied={setAppliedJobState}
                        compact
                        onTailorCv={isAuthenticated ? handleOpenTailorCv : undefined}
                      />
                    ) : null}

                    <div
                      className={
                        hasOverviewRightColumn
                          ? 'lg:grid lg:grid-cols-2 lg:gap-6 lg:items-start mb-6'
                          : 'mb-6'
                      }
                    >
                      <div className="flex flex-col gap-4 min-w-0">
                    {/* Prominent Compensation Display */}
                    {displaySalary && (
                      <div
                        className={`p-5 rounded-2xl bg-gradient-to-br from-[#C8DDCE]/60 to-[#F2EADF] dark:from-[#1A2E22] dark:to-[#2A1D0F] border-2 border-[#3B5E45]/25 dark:border-[#4A3520] shadow-lg dark:shadow-none ${!compensationExpanded ? 'cursor-pointer hover:cursor-pointer hover:border-[#3B5E45]/40 dark:hover:border-[#98CFA8]/40 lg:cursor-default' : ''}`}
                        role={!compensationExpanded ? 'button' : undefined}
                        tabIndex={!compensationExpanded ? 0 : undefined}
                        onClick={!compensationExpanded ? () => {
                          if (typeof window !== 'undefined' && window.matchMedia('(min-width: 1024px)').matches) return;
                          setCompensationExpanded(true);
                        } : undefined}
                        onKeyDown={!compensationExpanded ? (e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setCompensationExpanded(true);
                          }
                        } : undefined}
                        aria-expanded={compensationExpanded}
                        aria-label={!compensationExpanded
                          ? intl.formatMessage({ id: 'jobDetailPage.expandCompensation', defaultMessage: 'Expand compensation details' })
                          : undefined}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2 flex-wrap">
                            <div className="text-xs font-semibold text-jc-accent uppercase tracking-wide">
                              <FormattedMessage id="jobDetailPage.compensation" defaultMessage="Compensation" description="Section label for job compensation information (salary, benefits, vacation)" />
                            </div>
                            {salarySourceBadgeKind ? (
                              <JobSalarySourceBadge kind={salarySourceBadgeKind} />
                            ) : null}
                            <button
                              type="button"
                              onClick={() => setCompensationExpanded(!compensationExpanded)}
                              className="lg:hidden text-jc-accent hover:brightness-90 transition-transform cursor-pointer hover:cursor-pointer p-0.5 rounded-lg hover:bg-jc-accent-muted/50"
                              aria-label={compensationExpanded
                                ? intl.formatMessage({ id: 'jobDetailPage.collapseCompensation', defaultMessage: 'Collapse compensation details' })
                                : intl.formatMessage({ id: 'jobDetailPage.expandCompensation', defaultMessage: 'Expand compensation details' })}
                            >
                              {compensationExpanded ? (
                                <CaretUp className="h-4 w-4" />
                              ) : (
                                <CaretDown className="h-4 w-4" />
                              )}
                            </button>
                          </div>
                          {!compensationExpanded ? (
                            <p className="lg:hidden mt-1 mb-1 flex items-start gap-1.5 text-[11px] text-jc-muted leading-snug">
                              <Info className="h-3.5 w-3.5 text-jc-accent/70 shrink-0 mt-0.5" aria-hidden />
                              <FormattedMessage
                                id="jobDetailPage.interactiveExpandHint"
                                defaultMessage="Tap this card for salary charts and full compensation details."
                                description="Visual hint that the compensation block expands on tap (mobile)"
                              />
                            </p>
                          ) : null}
                          {shouldShowOriginalButton && compensationExpanded && (
                            <button
                              type="button"
                              onClick={() => setShowOriginalSalary(!showOriginalSalary)}
                              className="text-xs text-jc-accent hover:text-jc-cta-hover underline cursor-pointer"
                            >
                              {showOriginalSalary ? (
                                <FormattedMessage id="jobDetailPage.showConverted" defaultMessage="Show EUR/month" description="Button label to display salary converted to EUR per month" />
                              ) : (
                                <FormattedMessage id="jobDetailPage.seeOriginal" defaultMessage="See original" description="Button label to view original salary amount before conversion" />
                              )}
                            </button>
                          )}
                        </div>
                        <div className="text-3xl font-bold text-jc-ink">
                          <SalaryInferenceExplainer explanationText={salaryInferenceExplanationText}>
                            <span className="inline-flex flex-wrap items-baseline gap-x-1">
                              {formatSalaryMinMaxPair(
                                displaySalary.min,
                                displaySalary.max,
                                (n) => `${getCurrencySymbol(displaySalary.currency)}${n.toLocaleString()}`,
                              )}
                              <span className="text-lg font-normal text-jc-accent">
                                /{displaySalary.period === 'annual'
                                  ? intl.formatMessage({ id: 'jobDetailPage.period.year', defaultMessage: 'year', description: 'Time period unit for salary (per year)' })
                                  : displaySalary.period === 'monthly'
                                    ? intl.formatMessage({ id: 'jobDetailPage.period.month', defaultMessage: 'month', description: 'Time period unit for salary (per month)' })
                                    : displaySalary.period === 'daily'
                                      ? intl.formatMessage({ id: 'jobDetailPage.period.day', defaultMessage: 'day', description: 'Time period unit for salary (per day)' })
                                      : intl.formatMessage({ id: 'jobDetailPage.period.hour', defaultMessage: 'hour', description: 'Time period unit for salary (per hour)' })}
                              </span>
                            </span>
                          </SalaryInferenceExplainer>
                        </div>
                        {/* Est. Finnish take-home pay: sits directly under the gross figure so the two read as related. */}
                        {finnishNetSalaryEstimate ? (
                          <div className="mt-0.5 text-sm">
                            <SalaryInferenceExplainer explanationText={finnishNetSalaryExplanationText}>
                              <span className="inline-flex flex-wrap items-baseline gap-x-1 text-jc-muted">
                                <FormattedMessage
                                  id="jobDetailPage.estimatedNetSalary.label"
                                  defaultMessage="Est. take-home pay"
                                  description="Label for the estimated after-tax Finnish salary shown near the gross compensation figure"
                                />
                                <span className="font-semibold text-jc-ink">
                                  {formatSalaryMinMaxPair(
                                    Math.round(finnishNetSalaryEstimate.netMin),
                                    Math.round(finnishNetSalaryEstimate.netMax),
                                    (n) => `€${n.toLocaleString()}`,
                                  )}
                                </span>
                                <span className="text-xs">
                                  /{intl.formatMessage({ id: 'jobDetailPage.period.month', defaultMessage: 'month', description: 'Time period unit for salary (per month)' })}
                                </span>
                              </span>
                            </SalaryInferenceExplainer>
                          </div>
                        ) : null}
                        {!compensationExpanded ? (
                          <div className="mt-2 rounded-xl border border-[#3B5E45]/20 dark:border-[#4A3520] bg-white/55 dark:bg-[#1F1509]/70 p-2.5 lg:hidden">
                            <p className="text-sm font-semibold text-jc-ink">
                              <FormattedMessage
                                id="jobDetailPage.salaryCollapsedTitle"
                                defaultMessage="Expand to unlock full salary context"
                                description="Collapsed salary teaser title inviting users to expand details"
                              />
                            </p>
                            <p className="text-[11px] text-jc-muted mt-0.5">
                              <FormattedMessage
                                id="jobDetailPage.salaryCollapsedBody"
                                defaultMessage="See benchmark placement, pay-band comparison graph, and localized salary narrative."
                                description="Collapsed salary teaser body describing additional details available in expanded view"
                              />
                            </p>
                            {collapsedSalarySnapshot ? (
                              <>
                                <div className="mt-1.5 flex flex-wrap items-center gap-1.5 text-[10px]">
                                  <div className="rounded-md border border-jc-border/80 bg-jc-page/80 px-1.5 py-0.5">
                                    <span className="text-jc-muted">
                                      <FormattedMessage id="jobDetailPage.salaryPreview.marketMedian" defaultMessage="Market median" description="Label for market median in collapsed salary preview" />:
                                    </span>{' '}
                                    <span className="font-semibold text-jc-ink">
                                      {getCurrencySymbol(displaySalary.currency)}
                                      {Math.round(collapsedSalarySnapshot.marketMedian).toLocaleString()}
                                    </span>
                                  </div>
                                  {collapsedSalarySnapshot.offerMid !== null ? (
                                    <div className="rounded-md border border-jc-border/80 bg-jc-page/80 px-1.5 py-0.5">
                                      <span className="text-jc-muted">
                                        {salaryListingProvenance === 'employer_quote' ? (
                                          <FormattedMessage id="jobDetailPage.salaryPreview.listingMidpoint" defaultMessage="Listing midpoint" description="Midpoint of pay range when grounded in job-ad wording" />
                                        ) : (
                                          <FormattedMessage id="jobDetailPage.salaryPreview.dataMidpointUnverified" defaultMessage="Data midpoint (unverified)" description="Midpoint when pay exists without quoted ad text" />
                                        )}
                                        :
                                      </span>{' '}
                                      <span className="font-semibold text-jc-ink">
                                        {getCurrencySymbol(displaySalary.currency)}
                                        {Math.round(collapsedSalarySnapshot.offerMid).toLocaleString()}
                                      </span>
                                    </div>
                                  ) : (
                                    <div className="rounded-md border border-jc-border/80 bg-jc-page/80 px-1.5 py-0.5">
                                      <span className="text-jc-muted">
                                        <FormattedMessage id="jobDetailPage.salaryPreview.benchmarkSpread" defaultMessage="Benchmark spread" description="Label for benchmark spread in collapsed salary preview" />:
                                      </span>{' '}
                                      <span className="font-semibold text-jc-ink">
                                        {getCurrencySymbol(displaySalary.currency)}
                                        {Math.round(collapsedSalarySnapshot.p25Min).toLocaleString()}-{getCurrencySymbol(displaySalary.currency)}{Math.round(collapsedSalarySnapshot.p75Max).toLocaleString()}
                                      </span>
                                    </div>
                                  )}
                                  {collapsedSalarySnapshot.sampleSize ? (
                                    <div className="rounded-md border border-jc-border/80 bg-jc-page/80 px-1.5 py-0.5 text-jc-muted">
                                      n={collapsedSalarySnapshot.sampleSize.toLocaleString()}
                                    </div>
                                  ) : null}
                                </div>
                                <div className="mt-1.5">
                                  <div className="relative h-1.5 rounded-full bg-[#D8EADB] dark:bg-[#1A2E22] overflow-hidden">
                                    <div className="absolute left-[18%] right-[18%] top-0 bottom-0 bg-[#90BC9C] dark:bg-[#3B5E45]" />
                                    <span className="absolute left-1/2 -translate-x-1/2 top-[-1px] h-2.5 w-[2px] bg-[#1E1A14] dark:bg-[#F2EADF]" />
                                    {collapsedSalarySnapshot.offerMid !== null ? (
                                      <span
                                        className="absolute top-[-1px] h-2 w-2 rounded-full border border-[#1E1A14] dark:border-[#F2EADF] bg-[#F5EDD0] dark:bg-[#2A1E08]"
                                        style={{
                                          left: `${Math.max(0, Math.min(100, ((collapsedSalarySnapshot.offerMid - collapsedSalarySnapshot.p25Min) / Math.max(1, collapsedSalarySnapshot.p75Max - collapsedSalarySnapshot.p25Min)) * 100))}%`,
                                          transform: 'translateX(-50%)',
                                        }}
                                      />
                                    ) : null}
                                  </div>
                                </div>
                              </>
                            ) : (
                              <div className="mt-1.5 flex items-end gap-1 h-4" aria-hidden>
                                <span className="w-1/5 h-1.5 rounded bg-[#B6D4BC] dark:bg-[#1A2E22]" />
                                <span className="w-1/5 h-2 rounded bg-[#90BC9C] dark:bg-[#2A4533]" />
                                <span className="w-1/5 h-3 rounded bg-[#6A9E77] dark:bg-[#3B5E45]" />
                                <span className="w-1/5 h-2.5 rounded bg-[#4D7A5A] dark:bg-[#4D7A5A]" />
                                <span className="w-1/5 h-3.5 rounded bg-[#3B5E45] dark:bg-[#98CFA8]" />
                              </div>
                            )}
                          </div>
                        ) : null}
                        {/* Listing salary quote: single place (not repeated in a bottom section). Always visible here so mobile-collapsed users still see it. */}
                        {displaySalary && salaryDetails ? (
                          <div className="text-sm text-jc-muted italic mt-2">
                            {fixMojibake(salaryDetails)}
                          </div>
                        ) : null}
                        <div
                          aria-hidden={!compensationExpanded}
                          className={compensationExpanded ? 'mt-2' : 'max-h-0 overflow-hidden opacity-0 pointer-events-none lg:max-h-none lg:overflow-visible lg:opacity-100 lg:pointer-events-auto lg:mt-2'}
                        >
                            {((vacationWeeks != null && vacationWeeks > 0) ||
                              (displayJob?.benefits && displayJob.benefits.length > 0)) && (
                              <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1">
                                {vacationWeeks != null && vacationWeeks > 0 ? (
                                  <span className="text-xs text-jc-muted">
                                    <FormattedMessage
                                      id="jobDetailPage.vacationWeeks"
                                      defaultMessage="Vacation: {weeks} weeks"
                                      description="Label showing number of vacation weeks offered, where {weeks} is the number"
                                      values={{ weeks: vacationWeeks }}
                                    />
                                  </span>
                                ) : null}
                                {displayJob?.benefits && displayJob.benefits.length > 0 ? (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      scrollToSection('jobDetails-benefits', 20);
                                    }}
                                    className="text-xs text-jc-accent hover:text-jc-cta-hover underline cursor-pointer shrink-0"
                                  >
                                    <FormattedMessage id="jobDetailPage.viewBenefits" defaultMessage="View benefits" description="Button label to navigate to benefits section on job detail page" />
                                  </button>
                                ) : null}
                              </div>
                            )}
                            {/* Salary Distribution Chart */}
                            {(displayJob?.allSalaryStats || displayJob?.companySalaryStats || displayJob?.salaryStats || displayJob?.locationSalaryStats) && (
                              <SalaryDistributionChart
                                key={salaryChartRemountKey}
                                salaryStats={displayJob.companySalaryStats || displayJob.salaryStats}
                                locationSalaryStats={displayJob.locationSalaryStats}
                                allSalaryStats={displayJob.allSalaryStats}
                                jobSalaryMin={salaryRangeMin}
                                jobSalaryMax={salaryRangeMax}
                                currency={currency}
                                salaryPeriod={salaryPeriod}
                                locale={jobLocale}
                                jobLocation={location}
                                jobTitle={normalisedTitle || title}
                                jobRoleLevel={normalisedRoleLevel}
                                jobSalaryListingProvenance={salaryListingProvenance}
                              />
                            )}
                        </div>
                      </div>
                    )}

                    {/* Salary Distribution Chart for jobs without salary range */}
                    {(!salaryRangeMin || !salaryRangeMax || salaryRangeMin <= 0 || salaryRangeMax <= 0) &&
                      (displayJob?.allSalaryStats || displayJob?.companySalaryStats || displayJob?.salaryStats || displayJob?.locationSalaryStats) && (
                        <div
                          className={`p-5 rounded-2xl bg-gradient-to-br from-[#F5EDD0]/90 to-[#F2EADF] dark:from-[#2A1E08] dark:to-[#2A1D0F] border-2 border-[#B8860B]/25 dark:border-[#4A3520] shadow-lg dark:shadow-none ${!compensationExpanded ? 'cursor-pointer hover:cursor-pointer hover:border-[#B8860B]/45 dark:hover:border-[#D4A820]/45 lg:cursor-default' : ''}`}
                          role={!compensationExpanded ? 'button' : undefined}
                          tabIndex={!compensationExpanded ? 0 : undefined}
                          onClick={!compensationExpanded ? () => {
                            if (typeof window !== 'undefined' && window.matchMedia('(min-width: 1024px)').matches) return;
                            setCompensationExpanded(true);
                          } : undefined}
                          onKeyDown={!compensationExpanded ? (e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              setCompensationExpanded(true);
                            }
                          } : undefined}
                          aria-expanded={compensationExpanded}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <p className="text-sm font-semibold text-[#6E4F00] dark:text-[#D4A820]">
                              <FormattedMessage id="jobDetailPage.salaryContextOnly" defaultMessage="Salary context" description="Header shown for salary context card when a direct salary range is unavailable" />
                            </p>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setCompensationExpanded((prev) => !prev);
                              }}
                              className="lg:hidden text-[#6E4F00] dark:text-[#D4A820] hover:brightness-90 transition-transform cursor-pointer hover:cursor-pointer p-0.5 rounded-lg hover:bg-jc-accent-muted/40"
                              aria-label={compensationExpanded
                                ? intl.formatMessage({ id: 'jobDetailPage.collapseCompensation', defaultMessage: 'Collapse compensation details' })
                                : intl.formatMessage({ id: 'jobDetailPage.expandCompensation', defaultMessage: 'Expand compensation details' })}
                            >
                              {compensationExpanded ? <CaretUp className="h-4 w-4" /> : <CaretDown className="h-4 w-4" />}
                            </button>
                          </div>
                          {!compensationExpanded ? (
                            <p className="text-xs text-jc-muted mt-1 lg:hidden flex items-start gap-1.5 leading-snug">
                              <Info className="h-3.5 w-3.5 text-[#6E4F00]/80 dark:text-[#D4A820]/80 shrink-0 mt-0.5" aria-hidden />
                              <span>
                                <FormattedMessage
                                  id="jobDetailPage.salaryCollapsedBodyNoRange"
                                  defaultMessage="Expand to view market benchmark ranges, position in market, and salary narrative insights."
                                  description="Collapsed salary teaser body for cards without explicit offer range"
                                />
                              </span>
                            </p>
                          ) : null}
                          <div
                            aria-hidden={!compensationExpanded}
                            className={compensationExpanded ? 'mt-3' : 'max-h-0 overflow-hidden opacity-0 pointer-events-none lg:max-h-none lg:overflow-visible lg:opacity-100 lg:pointer-events-auto lg:mt-3'}
                          >
                            <SalaryDistributionChart
                              key={salaryChartRemountKey}
                              salaryStats={displayJob.companySalaryStats || displayJob.salaryStats}
                              locationSalaryStats={displayJob.locationSalaryStats}
                              allSalaryStats={displayJob.allSalaryStats}
                              jobSalaryMin={salaryRangeMin}
                              jobSalaryMax={salaryRangeMax}
                              currency={currency}
                              salaryPeriod={salaryPeriod}
                              locale={jobLocale}
                              jobLocation={location}
                              jobTitle={normalisedTitle || title}
                              jobRoleLevel={normalisedRoleLevel}
                              jobSalaryListingProvenance={salaryListingProvenance}
                            />
                          </div>
                        </div>
                      )}

                      </div>

                    {hasOverviewRightColumn ? (
                      <div className="flex flex-col gap-4 min-w-0 mt-6 lg:mt-0">
                    <ApplyButton
                      applyUrl={applyUrl}
                      isApplied={isApplied}
                      displayJob={displayJob}
                      applyBtnStyle={applyBtnStyle}
                      applyBtnHoverStyle={applyBtnHoverStyle}
                      effectiveBrandColour={effectiveBrandColour}
                      onSetApplied={setAppliedJobState}
                      compact
                      onTailorCv={isAuthenticated ? handleOpenTailorCv : undefined}
                    />
                    {/* Application Deadline / Estimated Expiry */}
                    <ApplicationDeadlineSection
                      formattedApplicationDate={formattedApplicationDate}
                      formattedEstimatedExpiryDate={formattedEstimatedExpiryDate}
                      formattedPostedDate={formattedPostedDate}
                      postedDaysAgo={postedDaysAgo}
                      formattedLastSeenDate={formattedLastSeenDate}
                      lastSeenDaysAgo={lastSeenDaysAgo}
                    />

                    {/* Prominent Location and Remoteness Display — compact tiles; merge when values duplicate */}
                    <div>
                      <div className={`grid gap-2 ${mergeLocationRemotenessTiles ? 'grid-cols-1' : 'grid-cols-2'}`}>
                        {mergeLocationRemotenessTiles ? (
                          <div className="p-2.5 rounded-xl bg-jc-page/90 border border-jc-border flex items-start gap-2.5 shadow-sm">
                            <div className="flex items-center gap-0.5 shrink-0 rounded-lg bg-jc-elevated px-1.5 py-1 border border-jc-border/70" aria-hidden>
                              <MapPin className="w-4 h-4 text-jc-gold" weight="duotone" />
                              <WifiHigh className="w-4 h-4 text-jc-cta" weight="duotone" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="text-[10px] font-semibold text-jc-muted uppercase tracking-wide mb-0.5">
                                <FormattedMessage id="jobDetailPage.locationAndRemoteness" defaultMessage="Location & work model" description="Combined label when location text matches remoteness (e.g. both Remote)" />
                              </div>
                              <div className="text-sm font-semibold text-jc-ink leading-snug">
                                {remoteness ? (
                                  <a href={facetHref('remoteness', remoteness)} className="underline decoration-jc-accent/40 underline-offset-2 hover:text-jc-accent">
                                    {fixMojibake(remoteness)}
                                  </a>
                                ) : null}
                              </div>
                              {(() => {
                                const { subtitleLocations } = prioritizeLocations(getLocationsForJobDetailDisplay(location));
                                if (subtitleLocations.length === 0) return null;
                                return (
                                  <div className="text-[11px] text-jc-muted mt-1 leading-snug">
                                    {subtitleLocations.map((loc, idx) => (
                                      <span key={loc + idx}>
                                        <a href={facetHref('location', loc)} className="underline decoration-jc-accent/35 underline-offset-2 hover:text-jc-accent">
                                          {fixMojibake(loc)}
                                        </a>
                                        {idx < subtitleLocations.length - 1 ? ', ' : ''}
                                      </span>
                                    ))}
                                  </div>
                                );
                              })()}
                            </div>
                          </div>
                        ) : (
                          <>
                            {/* Location */}
                            <div className="p-2.5 rounded-xl bg-[#F5EDD0]/70 dark:bg-[#2A1E08]/80 border border-[#B8860B]/25 dark:border-[#4A3520] shadow-sm dark:shadow-none">
                              <div className="flex items-start gap-2">
                                <div className="w-8 h-8 shrink-0 rounded-lg bg-[#F5EDD0] dark:bg-[#342415] flex items-center justify-center">
                                  <MapPin className="h-4 w-4 text-[#6E4F00] dark:text-[#D4A820]" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="text-[10px] font-semibold text-[#6E4F00] dark:text-[#D4A820] mb-0.5 uppercase tracking-wide leading-tight">
                                    <FormattedMessage id="jobDetailPage.location" defaultMessage="Location" description="Section label for job location information" />
                                  </div>
                                  {(() => {
                                    const locationsArray = getLocationsForJobDetailDisplay(location);
                                    if (locationsArray.length === 0) {
                                      return (
                                        <span className="text-[#3B5E45] dark:text-[#98CFA8] italic">
                                          <FormattedMessage id="jobDetailPage.noLocation" defaultMessage="No location" description="Fallback text when job location is not available" />
                                        </span>
                                      );
                                    }
                                    const { primaryLocations, subtitleLocations } = prioritizeLocations(locationsArray);
                                    return (
                                      <>
                                        {primaryLocations.length > 0 && (
                                          <div className="text-sm font-semibold text-[#1E1A14] dark:text-[#F2EADF] leading-snug">
                                            {primaryLocations.map((loc, idx) => (
                                              <span key={loc + idx}>
                                                <a href={facetHref('location', loc)} className="underline decoration-jc-gold/40 underline-offset-2 hover:text-[#6E4F00] dark:hover:text-[#D4A820]">
                                                  {fixMojibake(loc)}
                                                </a>
                                                {idx < primaryLocations.length - 1 && ', '}
                                              </span>
                                            ))}
                                          </div>
                                        )}
                                        {subtitleLocations.length > 0 && (
                                          <div className="text-[11px] text-[#6E4F00] dark:text-[#D4A820] mt-0.5 leading-snug">
                                            {subtitleLocations.map((loc, idx) => (
                                              <span key={loc + idx}>
                                                <a href={facetHref('location', loc)} className="underline decoration-jc-gold/40 underline-offset-2 hover:text-[#6E4F00] dark:hover:text-[#D4A820]">
                                                  {fixMojibake(loc)}
                                                </a>
                                                {idx < subtitleLocations.length - 1 && ', '}
                                              </span>
                                            ))}
                                          </div>
                                        )}
                                      </>
                                    );
                                  })()}
                                </div>
                              </div>
                            </div>

                            {/* Remoteness */}
                            <div className="p-2.5 rounded-xl bg-jc-accent-muted/50 border border-jc-accent/20 shadow-sm dark:bg-jc-accent-muted/20 dark:border-jc-border dark:shadow-none">
                              <div className="flex items-start gap-2">
                                <div className="w-8 h-8 shrink-0 rounded-lg bg-jc-accent-muted flex items-center justify-center">
                                  <WifiHigh className="h-4 w-4 text-jc-cta" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="text-[10px] font-semibold text-jc-cta mb-0.5 uppercase tracking-wide leading-tight">
                                    <FormattedMessage id="jobDetailPage.remoteness" defaultMessage="Remoteness" description="Section label for work model/remoteness information (remote, hybrid, onsite)" />
                                  </div>
                                  <div className="text-sm font-semibold text-jc-ink truncate leading-snug">
                                    {remoteness ? (
                                      <a href={facetHref('remoteness', remoteness)} className="underline decoration-jc-accent/40 underline-offset-2 hover:text-jc-accent">
                                        {fixMojibake(remoteness ?? '')}
                                      </a>
                                    ) : (
                                      <span className="text-jc-muted italic font-normal">
                                        <FormattedMessage id="jobDetailPage.noRemoteness" defaultMessage="Not specified" description="Fallback text when work model/remoteness is not specified" />
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Secondary Job Details - Unified Grid Layout */}
                    {(field || normalisedRoleLevel || peopleManagementTier || jobType || minimumYearsOfExperience || visaSponsor) && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {field && (
                          <div className="p-2.5 rounded-xl bg-[#F2EADF]/90 dark:bg-[#2A1D0F]/90 border border-[#DDD5C8]/70 dark:border-[#4A3520]/70 shadow-sm dark:shadow-none">
                            <div className="text-[10px] font-semibold text-[#5A4A3A] dark:text-[#B8A48C] mb-0.5 uppercase tracking-wide">
                              <FormattedMessage id="jobDetailPage.field" defaultMessage="Field" description="Section label for job field/domain (e.g., Software Development, Marketing)" />
                            </div>
                            <div className="text-sm font-semibold text-[#1E1A14] dark:text-[#F2EADF] leading-snug">
                              <a href={facetHref('field', field ?? '')} className="underline decoration-[#3B5E45]/40 underline-offset-2 hover:text-[#3B5E45] dark:hover:text-[#98CFA8]">
                                {fixMojibake(field ?? '')}
                              </a>
                            </div>
                          </div>
                        )}

                        {(normalisedRoleLevel || peopleManagementTier) && (
                          <div className="p-2.5 rounded-xl bg-[#F2EADF]/90 dark:bg-[#2A1D0F]/90 border border-[#DDD5C8]/70 dark:border-[#4A3520]/70 shadow-sm dark:shadow-none">
                            <div className="text-[10px] font-semibold text-[#5A4A3A] dark:text-[#B8A48C] mb-1 uppercase tracking-wide">
                              <FormattedMessage id="jobDetailPage.roleAndManagement" defaultMessage="Role & Management" description="Section label combining job role level and people management tier" />
                            </div>
                            <div className="space-y-1">
                              {normalisedRoleLevel && (
                                <div className="text-sm leading-snug">
                                  <span className="text-[#5A4A3A] dark:text-[#B8A48C] mr-1">
                                    <FormattedMessage id="jobDetailPage.roleLevel" defaultMessage="Role Level" description="Section label for job role level (junior, mid, senior, etc.)" />:
                                  </span>
                                  <a href={facetHref('normalisedRoleLevel', normalisedRoleLevel ?? '')} className="font-semibold text-[#1E1A14] dark:text-[#F2EADF] underline decoration-[#3B5E45]/40 underline-offset-2 hover:text-[#3B5E45] dark:hover:text-[#98CFA8]">
                                    {fixMojibake(normalisedRoleLevel ?? '')}
                                  </a>
                                </div>
                              )}
                              {peopleManagementTier && (
                                <div className="text-sm leading-snug">
                                  <span className="text-[#5A4A3A] dark:text-[#B8A48C] mr-1">
                                    <FormattedMessage id="jobDetailPage.peopleManagementTier" defaultMessage="Management Tier" description="Section label for people management responsibility level" />:
                                  </span>
                                  <span className="font-semibold text-[#1E1A14] dark:text-[#F2EADF]">
                                    {fixMojibake(peopleManagementTier ?? '')}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {jobType && (
                          <div className="p-2.5 rounded-xl bg-[#F2EADF]/90 dark:bg-[#2A1D0F]/90 border border-[#DDD5C8]/70 dark:border-[#4A3520]/70 shadow-sm dark:shadow-none">
                            <div className="text-[10px] font-semibold text-[#5A4A3A] dark:text-[#B8A48C] mb-0.5 uppercase tracking-wide">
                              <FormattedMessage id="jobDetailPage.jobType" defaultMessage="Job Type" description="Section label for job type (full-time, part-time, contract, etc.)" />
                            </div>
                            <div className="text-sm font-semibold text-[#1E1A14] dark:text-[#F2EADF] leading-snug">
                              <a href={facetHref('jobType', jobType ?? '')} className="underline decoration-[#3B5E45]/40 underline-offset-2 hover:text-[#3B5E45] dark:hover:text-[#98CFA8]">
                                {fixMojibake(jobType ?? '')}
                              </a>
                            </div>
                          </div>
                        )}

                        {minimumYearsOfExperience != null && minimumYearsOfExperience > 0 && (
                          <div className="p-2.5 rounded-xl bg-[#F2EADF]/90 dark:bg-[#2A1D0F]/90 border border-[#DDD5C8]/70 dark:border-[#4A3520]/70 shadow-sm dark:shadow-none">
                            <div className="text-[10px] font-semibold text-[#5A4A3A] dark:text-[#B8A48C] mb-0.5 uppercase tracking-wide">
                              <FormattedMessage id="jobDetailPage.experience" defaultMessage="Experience" description="Section label for required years of experience" />
                            </div>
                            <div className="text-sm font-semibold text-[#1E1A14] dark:text-[#F2EADF] leading-snug">
                              <FormattedMessage
                                id="jobDetailPage.minimumYearsOfExperience"
                                defaultMessage="{years} {years, plural, one {year} other {years}}"
                                values={{ years: minimumYearsOfExperience }}
                              />
                            </div>
                          </div>
                        )}

                        {visaSponsor && (
                          <div className="p-2.5 rounded-xl bg-[#F5EDD0]/70 dark:bg-[#2A1E08]/85 border border-[#B8860B]/25 dark:border-[#4A3520] shadow-sm dark:shadow-none">
                            <div className="text-[10px] font-semibold text-[#6E4F00] dark:text-[#D4A820] mb-0.5 uppercase tracking-wide">
                              <FormattedMessage id="jobDetailPage.visa" defaultMessage="Visa" description="Section label for visa sponsorship information" />
                            </div>
                            <div className="text-sm font-semibold text-[#1E1A14] dark:text-[#F2EADF] leading-snug">
                              <FormattedMessage
                                id="jobDetailPage.visaSponsor"
                                defaultMessage="✓ Sponsorship available"
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Match Score Details */}
                    {matchScoreData ? (
                      <MatchScoreBreakdown matchScore={matchScoreData} />
                    ) : null}

                    {/* Posted only when no deadline/expiry card (avoids duplicating Posted under Estimated Expiry) */}
                    {!(formattedApplicationDate || formattedEstimatedExpiryDate) && formattedPostedDate && (
                      <div className="text-[11px] text-[#7A6E5F] dark:text-[#B8A48C]">
                        <FormattedMessage id="hit.postedDate.detail" defaultMessage="Posted: {date}" values={{ date: formattedPostedDate }} description="Job posting date shown on job detail page" />
                      </div>
                    )}

                    {/* Languages and Specialisations - Unified Card Layout */}
                    {((requiredLanguages && requiredLanguages.length > 0) || (optionalLanguages && optionalLanguages.length > 0) || (specialisations && specialisations.length > 0)) && (
                      <div className="p-2.5 rounded-xl bg-[#F2EADF]/90 dark:bg-[#2A1D0F]/90 border border-[#DDD5C8]/70 dark:border-[#4A3520]/70 shadow-sm dark:shadow-none">
                        {/* Required Languages */}
                        {requiredLanguages && requiredLanguages.length > 0 && (
                          <div className="mb-2 last:mb-0">
                            <div className="text-[10px] font-semibold text-[#3D3529] dark:text-[#C4A882] mb-1 uppercase tracking-wide">
                              <FormattedMessage id="jobDetailPage.requiredLanguages" defaultMessage="Required Languages" description="Section heading for list of required languages for the job" />
                            </div>
                            <div className="flex flex-wrap gap-1.5">
                              {requiredLanguages.map((lang: string, idx: number) => (
                                <a key={lang + idx} href={facetHref('requiredLanguages', lang)} className="px-2 py-0.5 rounded-md bg-[#C8DDCE] dark:bg-[#1A2E22] text-[#3B5E45] dark:text-[#98CFA8] text-xs font-medium border border-[#3B5E45]/20 dark:border-[#4A3520] hover:brightness-95 transition-[filter]">
                                  {lang}
                                </a>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Optional Languages */}
                        {optionalLanguages && optionalLanguages.length > 0 && (
                          <div className="mb-2 last:mb-0">
                            <div className="text-[10px] font-semibold text-[#5A4A3A] dark:text-[#B8A48C] mb-1 uppercase tracking-wide">
                              <FormattedMessage id="jobDetailPage.optionalLanguages" defaultMessage="Optional Languages" description="Section heading for list of optional/preferred languages for the job" />
                            </div>
                            <div className="flex flex-wrap gap-1.5">
                              {optionalLanguages.map((lang: string, idx: number) => (
                                <a key={lang + idx} href={facetHref('requiredLanguages', lang)} className="px-2 py-0.5 rounded-md bg-[#F2EADF] dark:bg-[#2A1D0F] text-[#5A4A3A] dark:text-[#C4A882] text-xs font-medium border border-[#DDD5C8] dark:border-[#4A3520] hover:brightness-95 transition-[filter]">
                                  {lang}
                                </a>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Specialisations */}
                        {specialisations && specialisations.length > 0 && (
                          <div className="last:mb-0">
                            <div className="text-[10px] font-semibold text-[#3D3529] dark:text-[#C4A882] mb-1 uppercase tracking-wide">
                              <FormattedMessage id="jobDetailPage.specialisations" defaultMessage="Specialisations" description="Section heading for job specializations or focus areas" />
                            </div>
                            <div className="flex flex-wrap gap-1.5">
                              {specialisations.map((spec: string, idx: number) => (
                                <a key={spec + idx} href={facetHref('specialisations', spec)} className="px-2 py-0.5 rounded-md bg-[#DDD5C8]/80 dark:bg-[#2A1D0F] text-[#5A3E20] dark:text-[#D4B898] text-xs font-medium border border-[#C4A882]/40 dark:border-[#4A3520] hover:brightness-95 transition-[filter]">
                                  {spec}
                                </a>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                      </div>
                    ) : null}
                    </div>

                    {/* Job Description */}
                    {displayDescription && (
                      <div className="my-4">
                        <div className="mb-2 flex flex-wrap items-center gap-2">
                          <h3 className="font-bold text-lg text-[#1E1A14] dark:text-[#F2EADF]">
                            <FormattedMessage id="jobDetailPage.description" defaultMessage="Job Description" description="Section heading for full job description text" />
                          </h3>
                          {showDescriptionSourceDebug ? (
                            <span
                              className="inline-flex shrink-0 rounded-full border border-[#C4A882]/40 dark:border-[#4A3520] bg-[#F2EADF] dark:bg-[#2A1D0F] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[#3B5E45] dark:text-[#98CFA8]"
                              data-job-description-source={jobDescriptionBodySource}
                              title={
                                jobDescriptionBodySource === 'original'
                                  ? 'originalDescription — employer listing text'
                                  : 'description — derived copy (original empty for this locale)'
                              }
                            >
                              {jobDescriptionBodySource === 'original' ? (
                                <FormattedMessage
                                  id="jobDetailPage.descriptionSourceBadge.original"
                                  defaultMessage="Original"
                                  description="Small badge: body text is from originalDescription"
                                />
                              ) : (
                                <FormattedMessage
                                  id="jobDetailPage.descriptionSourceBadge.derived"
                                  defaultMessage="Summary"
                                  description="Small badge: body text is from derived description"
                                />
                              )}
                            </span>
                          ) : null}
                        </div>
                        <p className="text-[#3D3529] dark:text-[#C4A882] text-base whitespace-pre-line">{displayDescription}</p>
                      </div>
                    )}

                    {/* Contact Email Link */}
                    <ContactEmailSection
                      contactEmail={contactEmail}
                      showContactEmail={showContactEmail}
                      onShowContactEmail={() => setShowContactEmail(true)}
                      jobId={displayJob?.objectID}
                      companyId={companyId}
                    />
                  </div>

                  <CompanySection
                    displayJob={displayJob}
                    companyId={companyId}
                    companyIndustry={companyIndustry}
                    listingCompanyIndustry={listingCompanyIndustry}
                    companyAbout={companyAbout}
                    companyContactDetails={companyContactDetails}
                    companyRevenue={companyRevenue}
                    companyProfit={companyProfit}
                    companyProfitMargin={companyProfitMargin}
                    companyMonthlyCounts={companyMonthlyCounts}
                    companyHeadCountMin={companyHeadCountMin}
                    companyHeadCountMax={companyHeadCountMax}
                    companyHeadcountRangeMin={companyHeadcountRangeMin}
                    companyHeadcountRangeMax={companyHeadcountRangeMax}
                    companyHQInFinland={companyHQInFinland}
                    companyVideo={companyVideo}
                    linkedinUrl={linkedinUrl}
                    facebookUrl={facebookUrl}
                    instagramUrl={instagramUrl}
                    youtubeUrl={youtubeUrl}
                    tiktokUrl={tiktokUrl}
                    registryMainBusinessLineDescriptionEn={registryMainBusinessLineDescriptionEn}
                    registryMainBusinessLineDescriptionFi={registryMainBusinessLineDescriptionFi}
                    registryMainBusinessLineDescriptionSv={registryMainBusinessLineDescriptionSv}
                    registryCompanyFormDescription={registryCompanyFormDescription}
                    registryWebsite={registryWebsite}
                    registryMainName={registryMainName}
                    registryStreet={registryStreet}
                    registryCity={registryCity}
                    registryPostCode={registryPostCode}
                    registryBuildingNumber={registryBuildingNumber}
                    address={address}
                    address_fi={address_fi}
                    address_sv={address_sv}
                    businessID={businessID}
                    registryBusinessId={registryBusinessId}
                    companyGeocoordinates={companyGeocoordinates}
                    currentLocaleTrends={currentLocaleTrends}
                    companyHiringTrendsByLocale={displayJob?.companyHiringTrendsByLocale as any}
                    jobLocale={jobLocale}
                    brandColour={effectiveBrandColour}
                    useBrandColor={useBrandColor}
                    hasAnyCharts={hasAnyCharts}
                    chartsCollapsed={chartsCollapsed}
                    onToggleChartsCollapse={toggleChartsCollapse}
                    companyRef={companyRef}
                    isTyomarkkinatori={displayJob?.isTyomarkkinatori}
                  />

                  <JobDetailsSection
                    displayJob={displayJob}
                    optionalLanguages={optionalLanguages}
                    perks={perks}
                    jobDetailsRef={jobDetailsRef}
                  />

                  {/* Similar jobs section */}
                  <SimilarJobsSection similarJobs={effectiveSimilarJobs} seeAllHref={similarJobsSeeAllHref} />

                  {/* Related internal links (company, industry, skills, salary, content guides) */}
                  <InternalLinksSection links={allLinks} />

                  {/* Salary quote from listing text when we have no numeric range in the card (avoids duplicating salaryDetails under compensation). */}
                  {salaryDetails && !displaySalary ? (
                    <div className="mt-6">
                      <h3 className="font-bold text-lg text-[#1E1A14] dark:text-[#F2EADF] mb-2">
                        <FormattedMessage
                          id="jobDetailPage.salaryDetails"
                          defaultMessage="Salary Details"
                          description="Section heading for detailed salary information and compensation breakdown"
                        />
                      </h3>
                      <p className="text-sm text-[#3D3529] dark:text-[#C4A882] whitespace-pre-line">{fixMojibake(salaryDetails)}</p>
                    </div>
                  ) : null}
                </div>
              </div>
            </main>
            )}
          </div>

          {displayJob?.isTyomarkkinatori === true ? (
            <div className="max-w-6xl mx-auto px-2 sm:px-6">
              <TyomarkkinatoriDataSourceAttribution />
            </div>
          ) : null}

          {displayJob ? (
            <div className="max-w-6xl mx-auto px-2 sm:px-6 pb-4 w-full">
              <EngagementStrip
                feedbackSource="job_detail"
                variant="card"
                feedbackMetadata={{
                  jobId: String(displayJob.objectID),
                  companyId: displayJob.companyId,
                  title: displayJob.title ?? displayJob.normalisedTitle,
                  locale: jobLocale,
                }}
              />
            </div>
          ) : null}

          {displayJob ? (
            <TailorCvModal
              job={displayJob}
              applyUrl={applyUrl}
              isOpen={showTailorCvModal}
              onClose={() => setShowTailorCvModal(false)}
            />
          ) : null}

          {/* Footer - Full width outside main content container */}
          <Footer />

        </div>
    );
  };

export default JobDetailPage;